<!-- Loading state -->
<!-- <div class="container" *ngIf="loading">
  <app-skeleton-loader type="detail" message="Loading job details..."></app-skeleton-loader>
</div> -->

<!-- Error state -->
<div class="container" *ngIf="error && !loading">
  <app-error
    title="{{ 'job.detail.error.title' | transloco }}"
    message="{{ 'job.detail.error.message' | transloco }}"
    [showBackLink]="true"
    backLinkText="{{ 'job.detail.error.backLinkText' | transloco }}"
    backLinkRoute="/jobs"></app-error>
</div>

<!-- Job detail content - only show when not loading and no error -->
<ng-container *ngIf="!loading && !error && job">
  <section class="job-detail-hero">
    <div class="container">
      <div>
        <div class="breadcrumbs">
          <a routerLink="{{ getLocalizedRoute('/') }}">{{ 'breadcrumbs.home' | transloco }}</a>
          <i class="fas fa-chevron-right"></i>
          <span>{{ 'job.detail.pageTitle' | transloco }}</span>
        </div>
        <h1 class="job-detail-hero__title">{{ job.title }}</h1>
        <div class="job-detail-hero__company">
          <ng-container *ngIf="job.tenantLogoUrl; else companyLogoPlaceholder">
            <img [src]="job.tenantLogoUrl" [alt]="job.tenantName" />
          </ng-container>
          <ng-template #companyLogoPlaceholder>
            <div class="company-logo-placeholder">
              <i class="fas fa-building"></i>
            </div>
          </ng-template>
          <span>{{ job.tenantName }}</span>
        </div>
      </div>
      <!-- Match Score (only for logged-in users when coming from recommended jobs) -->
      <div class="job-detail-hero__match" *ngIf="matchScore !== null && isLoggedIn">
        <div class="match-content">
          <span class="match-label">{{ 'pages.jobs.recommendedJobs.matchScore' | transloco }}</span>
          <div class="match-score-value" [ngClass]="matchScore | matchScoreClass">{{ matchScore | matchScoreNormalized }}%</div>
        </div>
        <div class="match-progress">
          <div class="match-progress-bar" [style.width.%]="matchScore | matchScoreNormalized" [ngClass]="matchScore | matchScoreClass"></div>
        </div>
      </div>
    </div>
  </section>

  <!-- Main Job Detail Content -->
  <section class="job-detail">
    <div class="container">
      <!-- Job Highlights - Styled like the image -->
      <div class="job-highlights-section">
        <div class="job-highlights">
          <div class="job-highlights__grid">
            <app-job-highlight-item
              icon="fa-euro-sign"
              label="{{ 'job.salary' | transloco }}"
              [value]="
                (job.salary.basicSalary || 0) +
                (job.salary.upperSalary ? ' - ' + job.salary.upperSalary : '') +
                ' ' +
                (job.salary.currency | currencyDisplay) +
                (job.salary.period ? ' / ' + ('jobSalaryPeriods.' + job.salary.period | transloco) : '')
              "></app-job-highlight-item>
            <app-job-highlight-item
              icon="fa-briefcase"
              label="{{ 'job.startDate' | transloco }}"
              value="{{ job.startDateAsap ? ('job.asap' | transloco) : job.startDate }}"></app-job-highlight-item>
            <app-job-highlight-item
              icon="fa-map-marker-alt"
              label="{{ 'job.placeOfWork' | transloco }}"
              [value]="getWorkplaceTypeDisplay(job.placeOfWork)"></app-job-highlight-item>
            <app-job-highlight-item
              icon="fa-clock"
              label="{{ 'job.employmentType' | transloco }}"
              [value]="getEmploymentTypeDisplay(job.type)"></app-job-highlight-item>
            <app-job-highlight-item
              icon="fa-calendar"
              label="{{ 'job.practiceYears' | transloco }}"
              value="{{ job.additionalInfo?.practiceYear || '0' }}"></app-job-highlight-item>
            <app-job-highlight-item
              icon="fa-user-graduate"
              label="{{ 'job.suitableForGraduate' | transloco }}"
              value="{{ job.additionalInfo?.graduate ? ('common.yes' | transloco) : ('common.no' | transloco) }}"></app-job-highlight-item>
          </div>
        </div>
      </div>

      <app-card [ai]="true">
        <div class="job-description">
          <div>
            <div class="d-flex gap-3 ai-center">
              <i class="fa-solid fa-wand-magic-sparkles job-detail__ai-icon"></i>
              <h2 class="job-detail__title">
                {{ 'job.detail.aiSummaryTitle' | transloco }}
              </h2>
            </div>
            <p class="job-detail__subtitle">{{ 'job.detail.aiSummarySubtitle' | transloco }}</p>
          </div>
          <div class="job-description__content">
            {{ displayedSummary }}
            <span *ngIf="shouldTruncateSummary" class="job-description__toggle" (click)="toggleSummary()">
              <span *ngIf="!isSummaryExpanded">{{ 'job.detail.showMore' | transloco }}</span>
              <span *ngIf="isSummaryExpanded">{{ 'job.detail.showLess' | transloco }}</span>
            </span>
          </div>
        </div>
      </app-card>

      <div class="job-detail-columns">
        <!-- Left Column: Main Content -->
        <div class="job-detail-main">
          <!-- Essential Information -->
          <app-card>
            <div class="job-table">
              <div>
                <h2 class="job-detail__title">{{ 'job.detail.essentialInformationTitle' | transloco }}</h2>
                <p class="job-detail__subtitle">{{ 'job.detail.essentialInformationSubtitle' | transloco }}</p>
              </div>
              <div class="job-table__section">
                <div class="job-table__item">
                  <div class="job-table__item-label">{{ 'job.startDate' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngIf="job.startDateAsap">{{ 'job.asap' | transloco }}</span>
                    <span *ngIf="!job.startDateAsap && job.startDate">{{ job.startDate }}</span>
                  </div>
                </div>

                <div class="job-table__item" *ngIf="job.salary">
                  <div class="job-table__item-label">{{ 'job.salary' | transloco }}</div>
                  <div class="job-table__item-value">
                    {{ job.salary.basicSalary }}{{ job.salary.upperSalary ? ' - ' + job.salary.upperSalary : '' }} {{ job.salary.currency | currencyDisplay }} /
                    {{ 'jobSalaryPeriods.' + job.salary.period | transloco }}
                  </div>
                </div>

                <div class="job-table__item" *ngIf="job.experience">
                  <div class="job-table__item-label">{{ 'job.experience' | transloco }}</div>
                  <div class="job-table__item-value">{{ job.experience === 'Not specified' ? ('common.notSpecified' | transloco) : job.experience }}</div>
                </div>

                <div class="job-table__item" *ngIf="job.benefits?.length">
                  <div class="job-table__item-label">{{ 'job.employeePerks' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngFor="let benefit of job.benefits">{{ benefit }}</span>
                  </div>
                </div>

                <div class="job-table__item" *ngIf="job.placeOfWork?.length">
                  <div class="job-table__item-label">{{ 'job.placeOfWork' | transloco }}</div>
                  <div class="job-table__item-value">
                    {{ getWorkplaceTypeDisplay(job.placeOfWork) }}
                  </div>
                </div>

                <div class="job-table__item" *ngIf="job.languageRequirements?.length">
                  <div class="job-table__item-label">{{ 'job.languageRequirements' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngFor="let lang of job.languageRequirements">
                      {{ lang.language | enumLabel: 'languages' }} ({{ lang.level }})
                    </span>
                  </div>
                </div>

                <div class="job-table__item" *ngIf="job.skills?.length">
                  <div class="job-table__item-label">{{ 'job.skills' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngFor="let skill of job.skills">{{ skill.skillName | enumLabel: 'skills' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </app-card>

          <!-- Job Description -->
          <app-card *ngIf="job.description">
            <div class="job-description">
              <div>
                <h2 class="job-detail__title">{{ 'job.detail.descriptionTitle' | transloco }}</h2>
                <p class="job-detail__subtitle">{{ 'job.detail.descriptionSubtitle' | transloco }}</p>
              </div>
              <div class="job-description__content" [innerHTML]="job.description | safeHtml"></div>
            </div>
          </app-card>

          <!-- Job requirements -->
          <app-card>
            <div class="job-table">
              <div>
                <h2 class="job-detail__title">{{ 'job.detail.requirementsTitle' | transloco }}</h2>
                <p class="job-detail__subtitle">{{ 'job.detail.requirementsSubtitle' | transloco }}</p>
              </div>
              <div class="job-table__section">
                <!-- Education -->
                <div class="job-table__item" *ngIf="job.education">
                  <div class="job-table__item-label">{{ 'job.education' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag">{{ job.education === 'Not specified' ? ('common.notSpecified' | transloco) : job.education }}</span>
                  </div>
                </div>

                <!-- Languages -->
                <div class="job-table__item" *ngIf="job.languageRequirements && job.languageRequirements.length > 0">
                  <div class="job-table__item-label">{{ 'job.languageRequirements' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngFor="let lang of job.languageRequirements">
                      {{ lang.language | enumLabel: 'languages' }} ({{ lang.level }})
                    </span>
                  </div>
                </div>

                <!-- Skills -->
                <div class="job-table__item" *ngIf="job.skills && job.skills.length > 0">
                  <div class="job-table__item-label">{{ 'job.skills' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag" *ngFor="let skill of job.skills">{{ skill.skillName | enumLabel: 'skills' }}</span>
                  </div>
                </div>

                <!-- Driver's License -->
                <div class="job-table__item" *ngIf="job.driverLicense">
                  <div class="job-table__item-label">{{ 'job.driverLicense' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag">{{ job.driverLicense }}</span>
                  </div>
                </div>

                <!-- Suitable for graduate -->
                <div class="job-table__item" *ngIf="job.additionalInfo?.graduate !== undefined">
                  <div class="job-table__item-label">{{ 'job.suitableForGraduate' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag">{{ job.additionalInfo?.graduate ? ('common.yes' | transloco) : ('common.no' | transloco) }}</span>
                  </div>
                </div>

                <!-- Practice Position -->
                <div class="job-table__item" *ngIf="job.additionalInfo?.practicePosition">
                  <div class="job-table__item-label">{{ 'job.practicePosition' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag">{{ job.additionalInfo?.practicePosition }}</span>
                  </div>
                </div>

                <!-- Practice Years -->
                <div class="job-table__item" *ngIf="job.additionalInfo?.practiceYear">
                  <div class="job-table__item-label">{{ 'job.practiceYears' | transloco }}</div>
                  <div class="job-table__item-value">
                    <span class="job-table__tag">{{ job.additionalInfo?.practiceYear }}</span>
                  </div>
                </div>

                <!-- Additional Personal Skills -->
                <div class="job-table__item" *ngIf="job.additionalInfo?.additionalPersonalSkills">
                  <div class="job-table__item-label">{{ 'job.additionalPersonalSkills' | transloco }}</div>
                  <div class="job-table__item-value">
                    <div [innerHTML]="job.additionalInfo?.additionalPersonalSkills | safeHtml"></div>
                  </div>
                </div>
              </div>
            </div>
          </app-card>

          <!-- TODO: keep for later -->
          <!-- Similar Jobs Section -->
          <!-- <app-card>
            <div class="similar-jobs">
              <h2 class="similar-jobs__title">Similar Jobs</h2>
              <p class="similar-jobs__placeholder">Similar job recommendations will be displayed here.</p>
            </div>
          </app-card> -->
        </div>

        <!-- Right Column: Sticky Sidebar -->
        <div class="job-detail-sidebar">
          <div class="job-detail-sidebar__sticky">
            <!-- Job Actions Card -->
            <app-card orientation="vertical">
              <h3 class="job-actions__title">{{ 'job.detail.options' | transloco }}</h3>

              <div class="job-actions__list">
                <a class="job-actions__item" (click)="copyJobLink()">
                  <i class="fas fa-link job-actions__icon"></i>
                  <span>{{ 'job.detail.copyLink' | transloco }}</span>
                </a>
              </div>

              <div class="job-actions__apply">
                <app-button
                  *ngIf="!job.isApplied"
                  routerLink="{{ getLocalizedRoute(`/job/${job.id}/apply`) }}"
                  queryParamsHandling="preserve"
                  size="lg"
                  [block]="true">
                  {{ 'job.applyNow' | transloco }}
                </app-button>
                <app-button *ngIf="job.isApplied" variant="outline" color="secondary" size="lg" [block]="true" [disabled]="true">
                  <i class="fas fa-check-circle"></i>
                  {{ 'job.applied' | transloco }}
                </app-button>
              </div>
            </app-card>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Mobile-only floating Apply CTA. Shown below the fold on touch
       devices so the user always has a one-tap way to apply without
       scrolling back to the sidebar. -->
  <a
    *ngIf="!job.isApplied"
    class="apply-fab"
    [routerLink]="getLocalizedRoute('/job/' + job.id + '/apply')"
    queryParamsHandling="preserve"
    [attr.aria-label]="'job.applyNow' | transloco">
    <i class="fas fa-paper-plane apply-fab__icon" aria-hidden="true"></i>
    <span class="apply-fab__label">{{ 'job.applyNow' | transloco }}</span>
  </a>
</ng-container>
